import { B, L, loc } from '@/i18n';
import type { Evaluation, EvidenceSource, FailureType, Mistake, Question, SkillView } from '@/core/types';
import { catalog } from '@/data/catalog';
import { uid } from '@/core/util/misc';
import { snapshot } from './questions/generate';
import { describeResponse } from './questions/evaluate';
import type { ResponseValue } from '@/core/types';

/**
 * MISTAKE → DIAGNOSIS → PREREQUISITE RECOVERY → TARGETED PRACTICE → RE-TEST → MASTERY UPDATE
 */

export function diagnose(q: Pick<Question, 'skillId' | 'difficulty'> & { involvesSkills?: string[] }, ev: Evaluation, views: Record<string, SkillView>): { diagnosis: FailureType; suspected: string[] } {
  const prereqs = catalog.prerequisites(q.skillId);
  const involved = (q.involvesSkills ?? []).filter((s) => catalog.hasSkill(s));
  const candidates = [...new Set([...involved, ...prereqs])];
  const suspected = candidates
    .filter((p) => {
      const v = views[p];
      return !v || v.status === 'WEAK' || v.status === 'DEVELOPING' || v.status === 'UNKNOWN' || v.status === 'ASSESSED';
    })
    .sort((a, b) => (views[a]?.mastery ?? 0) - (views[b]?.mastery ?? 0));
  if (ev.misconceptionId) return { diagnosis: 'misconception', suspected };
  if (candidates.some((p) => views[p]?.status === 'WEAK')) return { diagnosis: 'prerequisite-gap', suspected };
  const own = views[q.skillId];
  if (own?.ability !== null && own?.ability !== undefined && q.difficulty >= own.ability + 1) return { diagnosis: 'difficult-task', suspected };
  return { diagnosis: 'insufficient-evidence', suspected };
}

export function misconceptionText(skillId: string, misconceptionId?: string) {
  if (!misconceptionId) return undefined;
  // search the skill and its prerequisites/related skills for the misconception definition
  const ids = [skillId, ...catalog.ancestors(skillId)];
  for (const id of ids) {
    const m = catalog.skill(id)?.content.misconceptions.find((x) => x.id === misconceptionId);
    if (m) return m;
  }
  for (const s of catalog.skills) {
    const m = s.content.misconceptions.find((x) => x.id === misconceptionId);
    if (m) return m;
  }
  return undefined;
}

export function createOrUpdateMistake(
  existing: Mistake[], q: Question, r: ResponseValue | null, ev: Evaluation, views: Record<string, SkillView>, source: EvidenceSource, now: number, reasoning?: string,
): { mistake: Mistake; isNew: boolean } {
  const same = existing.find((m) => m.status !== 'resolved' && m.skillId === q.skillId && (m.question.fingerprint === q.fingerprint || (ev.misconceptionId && m.misconceptionId === ev.misconceptionId)));
  const { diagnosis, suspected } = diagnose(q, ev, views);
  if (same) {
    return {
      isNew: false,
      mistake: { ...same, occurrences: same.occurrences + 1, at: now, diagnosis, suspectedPrerequisites: suspected, status: 'diagnosed', recovery: { ...same.recovery, lastRetestCorrect: false } },
    };
  }
  const mis = misconceptionText(q.skillId, ev.misconceptionId);
  return {
    isNew: true,
    mistake: {
      id: uid('mis'),
      at: now,
      skillId: q.skillId,
      subjectId: q.subjectId,
      question: snapshot(q),
      responseText: describeResponse(q, r),
      expected: ev.expected,
      errorType: ev.errorType ?? 'unknown',
      misconceptionId: ev.misconceptionId,
      diagnosis,
      suspectedPrerequisites: suspected,
      reasoning,
      correctionNote: mis ? `${mis.why} ${L('Fix', 'Slik gjør du')}: ${mis.fix}` : undefined,
      status: 'diagnosed',
      recovery: { prerequisiteChecked: [], targetedPractice: 0, retests: 0 },
      source,
      occurrences: 1,
    },
  };
}

/** Progress open mistakes after a correct answer. Returns updated mistakes and resolved ids. */
export function progressRecovery(mistakes: Mistake[], skillId: string, difficulty: number, hints: number, now: number): { mistakes: Mistake[]; resolved: Mistake[] } {
  const resolved: Mistake[] = [];
  const updated = mistakes.map((m) => {
    if (m.status === 'resolved') return m;
    // practising a suspected prerequisite counts as recovery work
    if (m.suspectedPrerequisites.includes(skillId)) {
      const checked = m.recovery.prerequisiteChecked.includes(skillId) ? m.recovery.prerequisiteChecked : [...m.recovery.prerequisiteChecked, skillId];
      return { ...m, status: 'recovering' as const, recovery: { ...m.recovery, prerequisiteChecked: checked } };
    }
    if (m.skillId !== skillId) return m;
    const practice = m.recovery.targetedPractice + 1;
    const isRetest = difficulty >= m.question.difficulty - 1 && hints === 0;
    if (isRetest && practice >= 1) {
      const r: Mistake = { ...m, status: 'resolved', resolvedAt: now, recovery: { ...m.recovery, targetedPractice: practice, retests: m.recovery.retests + 1, lastRetestCorrect: true } };
      resolved.push(r);
      return r;
    }
    return { ...m, status: practice >= 2 ? 'retest' as const : 'recovering' as const, recovery: { ...m.recovery, targetedPractice: practice } };
  });
  return { mistakes: updated, resolved };
}

export function errorTypeLabel(t: string): string {
  const m: Record<string, [string, string]> = {
    conceptual: ['conceptual', 'begrepsmessig'], procedural: ['procedural', 'fremgangsmåte'], computational: ['computational', 'regnefeil'], sign: ['sign', 'fortegn'],
    misread: ['misread', 'feillest'], prerequisite: ['prerequisite', 'forkunnskap'], vocabulary: ['vocabulary', 'ordforråd'], grammar: ['grammar', 'grammatikk'],
    spelling: ['spelling', 'rettskriving'], incomplete: ['incomplete', 'ufullstendig'], reasoning: ['reasoning', 'resonnering'], unknown: ['unknown', 'ukjent'],
  };
  const v = m[t];
  return v ? L(v[0], v[1]) : t;
}

export const DIAGNOSIS_LABEL: Record<FailureType, string> = loc({
  'difficult-task': B('Task above current level', 'Oppgaven var over nåværende nivå'),
  'prerequisite-gap': B('Missing prerequisite', 'Mangler forkunnskap'),
  misconception: B('Misconception', 'Misforståelse'),
  'insufficient-evidence': B('Needs more evidence', 'Trenger mer informasjon'),
});
